import { inject, Injectable } from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import { SCREENING_CONFIG_API_SERVICE_TOKEN } from '@nk/api-lib';
import {
  ScreeningCaseCompositeEntity,
  ScreeningCaseReviewCompositeEntity,
  ScreeningCaseReviewEntity,
  ScreeningCaseReviewStatusEntity,
  ScreeningCaseReviewStatusKey,
  WithUuid,
} from '@nk/data-entities-lib';
import { NkToastrService } from '@nk/ui-lib';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { ScreeningCaseReviewFormValue } from '../utils/screening-case-review.utils';
import { MonitoringConnectionService } from './monitoring-connection.service';
import { MonitoringOriginEntityService } from './monitoring-origin-entity.service';
import { ScreeningCaseService } from './screening-case.service';

export interface SaveReviewResult {
  success: boolean;
}

export interface CreateReviewResult {
  success: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ScreeningCaseReviewEditorService {
  private readonly _screeningCaseService = inject(ScreeningCaseService);
  private readonly _appService = inject(AppService);
  private readonly _monitoringOriginEntityService = inject(MonitoringOriginEntityService);
  private readonly _monitoringConnectionService = inject(MonitoringConnectionService);
  private readonly _screeningConfigService = inject(SCREENING_CONFIG_API_SERVICE_TOKEN);
  private readonly _toast = inject(NkToastrService);
  private readonly _translateService = inject(TranslateService);

  public saveReview(
    screeningConfigUuid: string,
    reviewComposite: ScreeningCaseReviewCompositeEntity,
    statusList: ScreeningCaseReviewStatusEntity[],
    formValue: ScreeningCaseReviewFormValue,
    commitReview = false,
  ): Observable<SaveReviewResult> {
    const saveTimestamp = new Date();
    const payload: WithUuid<ScreeningCaseReviewEntity> = {
      ...reviewComposite.screening_case_review,
      ...formValue,
      save_timestamp: saveTimestamp,
    };

    if (commitReview) {
      const status = statusList.find(
        (i) => i.screening_case_review_status_key === ScreeningCaseReviewStatusKey.complete,
      );
      if (!status?.uuid) {
        throw new Error('screening_case_review_status_key === complete was not found');
      }
      payload.screening_case_review_status_uuid = status.uuid;
      payload.complete_timestamp = saveTimestamp;
    } else {
      const status = statusList.find(
        (i) => i.screening_case_review_status_key === ScreeningCaseReviewStatusKey.ongoing,
      );
      if (!status?.uuid) {
        throw new Error('screening_case_review_status_key === ongoing was not found');
      }
      payload.screening_case_review_status_uuid = status.uuid;
    }

    return this._screeningCaseService.patchScreeningCaseReviewEntity(screeningConfigUuid, payload.uuid, payload).pipe(
      tap(() => {
        this._invalidateCaches();
        this._toast.success(
          this._translateService.instant(extractTranslation('common.request_success.title')) as string,
        );
      }),
      map(() => ({ success: true })),
    );
  }

  public createReview(
    screeningConfigUuid: string,
    caseComposite: ScreeningCaseCompositeEntity,
    statusList: ScreeningCaseReviewStatusEntity[],
  ): Observable<CreateReviewResult> {
    const status = statusList.find((i) => i.screening_case_review_status_key === 'pending');

    const appMembershipUuid$ = this._appService.identityDataCache
      .get()
      .pipe(map((identity) => identity.app_membership.uuid));

    let assigneeAppMembershipUuid$: Observable<string>;
    if (!caseComposite.screening_case.assignee_app_membership_uuid) {
      // Assign case first, if not yet assigned
      assigneeAppMembershipUuid$ = appMembershipUuid$.pipe(
        switchMap((appMembershipUuid) =>
          this._screeningCaseService
            .assignCase(screeningConfigUuid, caseComposite.screening_case.uuid, {
              assignee_app_membership_uuid: appMembershipUuid,
            })
            .pipe(map(() => appMembershipUuid)),
        ),
      );
    } else {
      assigneeAppMembershipUuid$ = of(caseComposite.screening_case.assignee_app_membership_uuid);
    }

    return assigneeAppMembershipUuid$.pipe(
      switchMap((assigneeAppMembershipUuid) => {
        const screeningCaseReview: Partial<ScreeningCaseReviewEntity> = {
          screening_case_uuid: caseComposite.screening_case.uuid,
          pep_confirmed_flag: false,
          screening_case_review_status_uuid: status?.uuid ?? undefined,
          entity_match_decision_type_uuid: undefined,
          reviewer_app_membership_uuid: assigneeAppMembershipUuid,
        };
        return this._screeningCaseService.postScreeningCaseReviewEntity(screeningConfigUuid, screeningCaseReview);
      }),
      tap(() => {
        this._invalidateCaches();
      }),
      map(() => ({ success: true })),
    );
  }

  private _invalidateCaches(): void {
    this._screeningCaseService.screeningCaseCompositeCache.invalidateAllCache();
    this._monitoringOriginEntityService.monitoringOriginEntityCompositeCollectionCache.invalidateAllCache();
    this._screeningCaseService.screeningEntityScreeningCaseCompositeCollectionCache.invalidateAllCache();
    this._monitoringConnectionService.monitoringConnectionCompositeCollectionCache.invalidateAllCache();
    this._screeningConfigService.screeningActionCompositeCollectionCache.invalidateAllCache();
    this._screeningConfigService.screeningConfigStatsAggregateCache.invalidateAllCache();
  }
}
